// The ticket page's view of "the list I came from" (see lib/ticket-nav.ts).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import {
  loadListContext,
  neighbours,
  planAdvance,
  readAutoAdvance,
  saveListContext,
  writeAutoAdvance,
  type ListContext,
} from './ticket-nav';
import { localStore, sessionStore } from './browser-storage';

/** Open the next ticket of the remembered list (or go back to the list at its end),
 *  dropping the finished ticket from it. False when there is no list for this ticket. */
export function advanceFrom(ticketId: string | undefined, navigate: NavigateFunction): boolean {
  const plan = planAdvance(loadListContext(sessionStore()), ticketId);
  if (!plan) return false;
  saveListContext(plan.ctx, sessionStore());
  navigate(plan.to);
  return true;
}

export function useTicketNav(ticketId: string | undefined) {
  const navigate = useNavigate();
  const [auto, setAutoState] = useState(() => readAutoAdvance(localStore()));
  const [ctx, setCtx] = useState<ListContext | null>(() => loadListContext(sessionStore()));
  // Another ticket (or the list) may have saved a newer list since this one mounted.
  useEffect(() => {
    setCtx(loadListContext(sessionStore()));
  }, [ticketId]);

  const nb = useMemo(() => neighbours(ctx, ticketId), [ctx, ticketId]);

  const setAuto = useCallback((on: boolean) => {
    setAutoState(on);
    writeAutoAdvance(localStore(), on);
  }, []);

  /** [ and ]: step through the list without finishing the ticket. */
  const step = useCallback(
    (dir: 'next' | 'prev') => {
      const id = dir === 'next' ? nb?.next : nb?.prev;
      if (id) navigate('/tickets/' + id);
    },
    [nb, navigate],
  );

  /** After Resolve / Snooze / Send: open the next ticket, if the agent wants that. */
  const maybeAdvance = useCallback(() => {
    if (!auto) return;
    if (advanceFrom(ticketId, navigate)) setCtx(loadListContext(sessionStore()));
  }, [auto, ticketId, navigate]);

  return { auto, setAuto, ctx, nb, step, maybeAdvance };
}
